import DOMPurify from 'dompurify';
/**
 * Copyright: ThoughtSpot Inc. 2019
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Lightweight UI tooltip
 *
 * To display a tooltip, populate "data-tooltip-content" attribute in your element.
 * Position can be configured by "data-tooltip-position" attribute. (default: 'top')
 * Alignment can be configured by "data-tooltip-align" attribute. (default: 'center')
 * Max Width (number) can be configured by "data-tooltip-max-width" attribute. (default 400px from _tooltip.scss)
 *
 * Examples:
 *
 * <button data-tooltip-content="Learn more">Hover here</button>
 *
 *
 * <button data-tooltip-content="Learn more"
 *         data-tooltip-position="bottom"
 *         data-tooltip-align="left"
 *         data-tooltip-max-width="500">
 *     Hover here
 * </button>
 *
 * Implementation details: We put a single div element in the body,
 * which is then populated with tooltip text and moved around.
 */
// Removing logger for now since we can't use it in blink-v2
// import {create} from '@thoughtspot/logger';
import _ from 'lodash';
import { Key } from 'w3c-keys';
import { DEBOUNCE_WAIT_MS, TOOLTIP_CONTAINER_ID, TOOLTIP_DELAY_MS } from './tooltip-constants';
import {
  calculateTooltipPosition,
  getAlignmentAttribute,
  getMaxWidthAttribute,
  getPositionAttribute,
  getTooltipContent,
} from './tooltip-utils';

// const logger = create('rd-tooltip');

class Tooltip {
  private tooltipContainer: HTMLDivElement | null = null;

  private targetEl: HTMLElement | null = null;

  private timer: any;

  constructor() {
    if (document.getElementById(TOOLTIP_CONTAINER_ID)) {
      // logger.warn('Tooltip container initialized twice! ' +
      //     'Please make sure initializeTooltip() is only called once.');
      return;
    }
    this.createTooltipContainer();
    this.addEventListenersToShowTooltip();
    this.addEventListenersToHideTooltip();
  }

  /**
   * Show tooltip
   *
   * @param {HTMLElement} targetEl - the target element that triggered showTooltip
   * @param {string} tooltipText - text to show in the tooltip
   */
  public show(targetEl: HTMLElement, tooltipText: string): void {
    this.targetEl = targetEl;
    /**
     * Point to the id of the tooltip container element (for accessibility)
     */
    this.targetEl.setAttribute('aria-describedby', TOOLTIP_CONTAINER_ID);
    if (!this.timer) {
      this.timer = setTimeout(() => {
        if (!this.isVisible(this.targetEl)) return;
        const position = getPositionAttribute(this.targetEl!);
        const align = getAlignmentAttribute(this.targetEl!);
        const maxWidth = getMaxWidthAttribute(this.targetEl!);
        if (maxWidth) {
          this.tooltipContainer!.style.maxWidth = `${maxWidth}px`;
        }
        const sanitizedTooltipText = DOMPurify.sanitize(tooltipText);
        this.tooltipContainer!.innerHTML = sanitizedTooltipText;
        this.tooltipContainer!.className = 'rd-tooltip';
        const { tooltipTop, tooltipLeft } = calculateTooltipPosition(
          this.targetEl!,
          this.tooltipContainer!,
          position,
          align,
          true
        );
        this.tooltipContainer!.style.top = `${tooltipTop}px`;
        this.tooltipContainer!.style.left = `${tooltipLeft}px`;
        this.tooltipContainer!.setAttribute('data-state', 'visible');
      }, TOOLTIP_DELAY_MS);
    }
  }

  /**
   * Hide tooltip
   */
  public hide(): void {
    if (this.targetEl) {
      this.targetEl.removeAttribute('aria-describedby');
      this.targetEl = null;
    }
    if (this.timer) {
      this.timer = clearTimeout(this.timer);
    }
    if (this.tooltipContainer) {
      this.tooltipContainer.setAttribute('data-state', 'hidden');
      this.tooltipContainer.innerText = '';
      this.tooltipContainer.className = 'rd-tooltip-hidden';
    }
  }

  /**
   * Create a div element and append it to the document body
   */
  private createTooltipContainer(): void {
    const divElement = document.createElement('div');
    divElement.id = TOOLTIP_CONTAINER_ID;
    divElement.className = 'rd-tooltip-hidden';
    divElement.setAttribute('role', 'tooltip');
    divElement.setAttribute('data-state', 'hidden');
    document.body.appendChild(divElement);
    this.tooltipContainer = divElement;
  }

  // eslint-disable-next-line class-methods-use-this
  private isVisible(elem: any): boolean {
    return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
  }

  /**
   * Add event listeners to document to show the tooltip.
   * Only called once during initialization.
   */
  private addEventListenersToShowTooltip(): void {
    const showTooltipCallback = (e: any) => {
      const tooltipContent = getTooltipContent(e);
      if (!tooltipContent) return;
      const { targetEl, tooltipText } = tooltipContent;
      if (tooltipText) {
        this.hide();
        this.show(targetEl, tooltipText);
        // Add a one-time mouseleave event listener to the target element
        // to handle hiding of the tooltip
        targetEl.addEventListener(
          'mouseleave',
          // eslint-disable-next-line no-shadow
          (_) => {
            this.hide();
          },
          { once: true }
        );
      }
    };
    const options = {
      capture: true,
      passive: true,
    };
    /**
     * Use event delegation (i.e. add event listeners to document)
     * to optimize performance and memory usage
     */
    document.addEventListener(
      'mouseover',
      _.debounce(showTooltipCallback, DEBOUNCE_WAIT_MS),
      options
    );

    const showTooltipOnKeyboardFocus = (e: any) => {
      if (!e.target) {
        return;
      }
      // Show tooltip only on keyboard focus, and not on mouse clicks (See SCAL-78986)
      // This relies on "focus-visible" package which we already use for CSS

      // in firefox <html> element is focusable as well
      // on document click showTooltipOnKeyboardFocus is triggered
      // but the target does not have classList or hasAttribute properties
      // hence the need for checking if these are present
      // https://allyjs.io/data-tables/focusable.html
      if (
        e.target.classList?.contains('focus-visible') ||
        (e.target.hasAttribute && e.target.hasAttribute('data-focus-visible-added'))
      ) {
        showTooltipCallback(e);
      }
    };
    document.addEventListener('focus', showTooltipOnKeyboardFocus, options);
  }

  /**
   * Add event listeners to document to hide tooltip.
   * Only called once during initialization.
   */
  private addEventListenersToHideTooltip(): void {
    /**
     * Hide the tooltip if the target element no longer exists.
     * We add a mutation observer to body to check this.
     */
    // eslint-disable-next-line no-shadow
    const mutationObserver = new MutationObserver((_) => {
      if (this.targetEl && !this.targetEl.isConnected) {
        this.hide();
      }
    });
    mutationObserver.observe(document.body, { childList: true });

    const options = {
      capture: true,
      passive: true,
    };

    /**
     * Hide the tooltip on scroll to prevent a floating tooltip
     * where it is detached from the target element.
     */
    document.addEventListener('scroll', () => this.hide(), options);

    const hideTooltipCallback = (e: any) => {
      if (!e.toElement && !e.relatedTarget) {
        return;
      }
      this.hide();
    };
    document.addEventListener('blur', hideTooltipCallback, options);
    document.addEventListener('mousedown', hideTooltipCallback, options);
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === Key.Escape) {
          this.hide();
        }
      },
      options
    );
  }
}

/**
 * Initialize a tooltip container and append it to document body.
 * Should only be initialized ONCE in: blink/app/src/base/tooltip/tooltip-service.ts
 */
export const initializeTooltip = () => new Tooltip();
